import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Divider,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage as policyErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function apiErrorMessage(err) {
  return policyErrorMessage(err, err?.message || 'Request failed');
}

export default function Settings() {
  const { user, loginWithToken, completePasswordChange } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [dob, setDob] = useState('');
  const [information, setInformation] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/patient/profile`)
      .then(({ data }) => {
        if (!cancelled) {
          setFullName(data.full_name || '');
          setEmail(data.email || '');
          setPhone(data.phone || '');
          setDob(data.date_of_birth || '');
          setInformation(data.information || '');
        }
      })
      .catch(() => toast.error('Could not load profile'))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const { data } = await axios.patch(`${API_URL}/api/v1/patient/profile`, {
        full_name: fullName,
        phone,
        date_of_birth: dob || null,
        information,
      });
      // Keep the name in the header in step. This reply carries only the handful of
      // fields it touched, so fold them into the profile rather than replacing it.
      if (data.user) {
        loginWithToken({ ...user, ...data.user });
      }
      toast.success('Profile updated');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const problem = passwordProblem(newPassword);
    if (problem) {
      toast.error(problem);
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    setSavingPw(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/auth/me/password`, {
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirm: confirmPassword,
      });
      // The change revokes every earlier token, this one included — adopt the new one.
      completePasswordChange(res.data);
      toast.success('Password updated. A confirmation email has been sent to your address.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSavingPw(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h4" sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700, mb: 0.5 }}>
        Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Update your profile and manage your password.
      </Typography>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(67, 20, 7, 0.12)', mb: 3 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Profile
        </Typography>
        <Box component="form" onSubmit={handleSaveProfile}>
          <Stack spacing={2}>
            <TextField
              label="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              fullWidth
              autoComplete="name"
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              fullWidth
              autoComplete="email"
              InputProps={{ readOnly: true }}
              helperText="Email cannot be changed here. Contact your clinic if you need a different address."
            />
            <TextField
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              fullWidth
              autoComplete="tel"
              placeholder="(555) 123-4567"
              inputProps={{ maxLength: 50 }}
            />
            <TextField
              label="Date of birth"
              type="date"
              value={dob}
              onChange={(e) => setDob(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
            />
            <TextField
              label="About you"
              value={information}
              onChange={(e) => setInformation(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder="Anything you want your care team to know (allergies reminder, communication preferences, etc.)."
              helperText="Optional."
            />
            <Button type="submit" variant="contained" disabled={savingProfile} sx={{ alignSelf: 'flex-start' }}>
              {savingProfile ? 'Saving…' : 'Save profile'}
            </Button>
          </Stack>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid rgba(67, 20, 7, 0.12)' }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          Change password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter your current password, then your new password twice. We will email you when your password changes.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box component="form" onSubmit={handleChangePassword}>
          <Stack spacing={2}>
            <TextField
              label="Current password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              fullWidth
              autoComplete="current-password"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowCurrentPassword((v) => !v)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="New password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              required
              helperText={PASSWORD_RULES_TEXT}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowNewPassword((v) => !v)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm new password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              autoComplete="new-password"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="outlined" disabled={savingPw} sx={{ alignSelf: 'flex-start' }}>
              {savingPw ? 'Updating…' : 'Update password'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}
